import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "./ambiente";
import type { MemberDoc } from "./schema";

export interface Member extends MemberDoc { uid: string }

export function watchMembers(cid: string, cb: (list: Member[]) => void): () => void {
  return onSnapshot(
    collection(db, "challenges", cid, "members"),
    (snap) => cb(snap.docs.map((d) => ({ uid: d.id, ...(d.data() as MemberDoc) }))),
    () => cb([]),
  );
}

/**
 * A MINHA ficha num desafio, uma leitura só.
 *
 * A Home precisa saber se eu já postei hoje em cada desafio da lista, e
 * `watchMembers` traria o grupo inteiro de cada um — dezenas de documentos
 * para responder uma pergunta sobre um. É `getDoc` e não `onSnapshot` de
 * propósito: a lista não precisa reagir ao vivo, ela é relida quando a
 * tela remonta, e um listener por desafio na tela de entrada é assinatura
 * que fica aberta a sessão toda.
 *
 * Devolve `null` quando não dá para ler — offline ou regra —, e quem chama
 * trata isso como "não sei", não como "não postou".
 */
export async function myMembership(cid: string, uid: string): Promise<Member | null> {
  try {
    const snap = await getDoc(doc(db, "challenges", cid, "members", uid));
    return snap.exists() ? { uid: snap.id, ...(snap.data() as MemberDoc) } : null;
  } catch {
    return null;
  }
}

/**
 * Classificação.
 *
 * Soma pelas CHAVES do `dayPoints`, não pela lista `days`. Nem todo ponto
 * vem de prato — cravar o preço de um prato alheio e fechar um evento
 * também pontuam, em dias que podem não ter prato nenhum. Somando por
 * `days`, esses pontos apareciam no total e sumiam ao filtrar o período.
 */
export function standings(members: Member[], from?: string, to?: string): Member[] {
  const noPeriodo = ([dia]: [string, number]) =>
    (!from || dia >= from) && (!to || dia <= to);

  return [...members]
    .map((m) => ({
      ...m,
      /* Sem período, vale o total que o SERVIDOR gravou: é o único que
         desconta os pontos trocados por vale-falta. Refazer aqui pela
         soma dos dias dava um número no topo do perfil e outro no
         detalhamento logo abaixo. Com período, soma dias e cravadas
         (`extraPoints`), a mesma conta da temporada. */
      totalPoints: !from && !to && typeof m.totalPoints === "number"
        ? m.totalPoints
        : [m.dayPoints ?? {}, m.extraPoints ?? {}]
          .flatMap((mapa) => Object.entries(mapa))
          .filter(noPeriodo)
          .reduce((s, [, v]) => s + (Number(v) || 0), 0),
      days: (m.days ?? []).filter((d) => (!from || d >= from) && (!to || d <= to)),
    }))
    .sort((a, b) =>
      b.totalPoints - a.totalPoints
      || b.days.length - a.days.length
      || a.name.localeCompare(b.name, "pt-BR"));
}
